import React from 'react';
import KeystoneLogo from '../components/KeystoneLogo';
import BrochureGate from '../components/BrochureGate';
import LinkedInCopyLibrary from '../components/LinkedInCopyLibrary';
import InstagramCaptionLibrary from '../components/InstagramCaptionLibrary';

/**
 * Marketing Playbook, internal-only 12-week content calendar.
 *
 * Gated behind the SAME backend access gate as /brochure
 * (uses BrochureGate with a separate sessionStorage key, but the
 * password lives only in backend/.env). NEVER linked from the public
 * site; only surfaced from the brochure toolbar so it's discoverable
 * to the founder but invisible to public crawlers.
 *
 * Structure:
 *   Page 1, Cover + content strategy framework
 *   Page 2, LinkedIn Month 1 (Weeks 1-4 · 12 posts)
 *   Page 3, LinkedIn Month 2 (Weeks 5-8 · 12 posts)
 *   Page 4, LinkedIn Month 3 (Weeks 9-12 · 12 posts)
 *   Page 5, Instagram pillars + execution guide + hashtag bank
 */

// ─── Palette (matches brochure) ──────────────────────────────────────
const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  ink: '#0A2540',
  slate: '#2A3B5A',
  muted: '#5B6B82',
  border: '#E5DFD3',
  bgSoft: '#F7F4EE',
  cream: '#FFF8E7',
  magenta: '#D6336C',
  magentaDeep: '#A22456',
  gold: '#C99A2E',
  emerald: '#1F7A4C',
  paper: '#FAFAF7',
};

// ─── Layout primitives ───────────────────────────────────────────────
const Page = ({ children, bg = '#fff', last = false }) => (
  <section
    className="playbook-page"
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: '14mm 14mm',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: COLORS.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm',
      left: '14mm',
      right: '14mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8.5pt',
      color: COLORS.muted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}
  >
    <span>
      <span style={{ color: COLORS.magenta }}>●</span>  Marketing Playbook · Q1 2026 ·
      <span style={{ color: COLORS.gold, fontWeight: 700 }}> Internal use only</span>
    </span>
    <span style={{ color: COLORS.navy, fontWeight: 700 }}>{pageNum} / 05</span>
  </div>
);

const PageFooter = () => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm',
      left: '14mm',
      right: '14mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8pt',
      color: COLORS.muted,
      borderTop: `1px solid ${COLORS.border}`,
      paddingTop: '4mm',
    }}
  >
    <span>Keystone by CloudVoro · LinkedIn + Instagram editorial calendar · 12-week rolling</span>
    <span style={{ color: COLORS.magenta, fontWeight: 600 }}>Confidential · Do not share externally</span>
  </div>
);

const SectionLabel = ({ children, color = COLORS.magenta }) => (
  <div
    style={{
      fontSize: '7.5pt',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      fontWeight: 700,
      color,
      marginBottom: '3mm',
    }}
  >
    {children}
  </div>
);

// ─── Page 1, Cover + Strategy ────────────────────────────────────────
const PageCover = () => (
  <Page bg={COLORS.cream}>
    <PageHeader pageNum="01" />
    <div style={{ marginTop: '18mm' }}>
      <SectionLabel>Q1 2026 · Editorial calendar</SectionLabel>
      <h1
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '40pt',
          fontWeight: 700,
          color: COLORS.navy,
          lineHeight: 1.0,
          margin: '0 0 8mm 0',
          letterSpacing: '-0.025em',
        }}
      >
        Keystone marketing playbook.<br />
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 500 }}>12 weeks. 36 posts. One voice.</em>
      </h1>
      <p style={{ fontSize: '11pt', color: COLORS.slate, lineHeight: 1.55, maxWidth: '170mm' }}>
        A rolling 12-week LinkedIn + Instagram plan to build Keystone's brand in Irish & UK food
        manufacturing. Every post mapped to a content pillar, every pillar mapped to a buyer mindset.
        No motivational quotes. No stock photos of laptops. Every post earns its place.
      </p>
    </div>

    {/* Strategy frame */}
    <div style={{ marginTop: '12mm' }}>
      <SectionLabel color={COLORS.navy}>The strategy in 4 lines</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          ['01 · Audience', 'Irish & UK food manufacturers (dairies, cheese, bakery, meat, prepared foods). Decision makers: owners, MDs, QA managers, ops directors. Not procurement. Not IT.'],
          ['02 · Voice', 'Founder-led. Floor-first. Specific over generic. Tell stories with names, dates and operational details. Avoid software-bro vocabulary.'],
          ['03 · Cadence', 'LinkedIn: Mon · Wed · Fri (3/wk · 36/quarter). Instagram: Tue · Thu · Sat (2-3/wk · ~30/quarter, organised by pillar not date).'],
          ['04 · Goal', 'Two demos booked per week by Week 6. Five inbound replies per week by Week 10. One reference call introduction per week by Week 12.'],
        ].map(([t, d]) => (
          <div
            key={t}
            style={{
              background: '#fff',
              border: `1px solid ${COLORS.border}`,
              borderLeft: `3px solid ${COLORS.magenta}`,
              padding: '4mm 5mm',
              borderRadius: 4,
            }}
          >
            <div style={{ fontSize: '9.5pt', fontWeight: 800, color: COLORS.navy, marginBottom: '1.5mm' }}>{t}</div>
            <div
              style={{ fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: d }}
            />
          </div>
        ))}
      </div>
    </div>

    {/* Pillar key */}
    <div style={{ marginTop: '9mm' }}>
      <SectionLabel color={COLORS.navy}>The 6 content pillars (rotated weekly)</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4mm' }}>
        {[
          ['P1 · Excel Truth', 'Operational pain. Polarising. Short.', COLORS.magenta],
          ['P2 · From the Floor', 'Cashel Blue + customer voice. Proof.', COLORS.emerald],
          ['P3 · Compliance Decoded', 'FSAI / BRCGS / HACCP / EU food safety, plain English.', COLORS.navy],
          ['P4 · Tactical Resource', 'Checklists, templates, ROI calculators, lead-gen.', COLORS.gold],
          ["P5 · Founder's POV", "Asif's commentary from the floor, humanise.", COLORS.magentaDeep],
          ['P6 · Product Demo', '60-sec screen recordings, before/after stills.', '#7B5CFF'],
        ].map(([t, d, c]) => (
          <div
            key={t}
            style={{
              padding: '3mm 4mm',
              background: '#fff',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              borderTop: `3px solid ${c}`,
            }}
          >
            <div style={{ fontSize: '8.5pt', fontWeight: 800, color: c, marginBottom: '1mm' }}>{t}</div>
            <div style={{ fontSize: '7.5pt', color: COLORS.slate, lineHeight: 1.4 }}>{d}</div>
          </div>
        ))}
      </div>
    </div>

    <PageFooter />
  </Page>
);

// ─── LinkedIn week table cell helpers ────────────────────────────────
const LiPostRow = ({ week, day, pillar, hook, body, cta, format, pillarColor }) => (
  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
    <td style={{ padding: '2.5mm 3mm', fontSize: '7.5pt', fontWeight: 700, color: COLORS.navy, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
      W{week}<br />
      <span style={{ fontWeight: 600, color: COLORS.muted, fontSize: '6.5pt', letterSpacing: '0.06em' }}>{day}</span>
    </td>
    <td style={{ padding: '2.5mm 3mm', verticalAlign: 'top' }}>
      <span
        style={{
          display: 'inline-block',
          padding: '0.5mm 2mm',
          background: pillarColor,
          color: '#fff',
          fontSize: '6.5pt',
          fontWeight: 700,
          letterSpacing: '0.05em',
          borderRadius: 3,
          whiteSpace: 'nowrap',
        }}
      >
        {pillar}
      </span>
    </td>
    <td style={{ padding: '2.5mm 3mm', verticalAlign: 'top' }}>
      <div style={{ fontSize: '8pt', fontWeight: 700, color: COLORS.navy, lineHeight: 1.25, marginBottom: '1mm' }}>{hook}</div>
      <div style={{ fontSize: '7pt', color: COLORS.slate, lineHeight: 1.4 }}>{body}</div>
    </td>
    <td style={{ padding: '2.5mm 3mm', fontSize: '7pt', color: COLORS.muted, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
      <div style={{ marginBottom: '0.5mm' }}><strong style={{ color: COLORS.navy }}>{format}</strong></div>
      <div style={{ color: COLORS.magenta, fontWeight: 600 }}>{cta}</div>
    </td>
  </tr>
);

const LiTableHead = () => (
  <thead>
    <tr style={{ background: COLORS.bgSoft, borderBottom: `2px solid ${COLORS.navy}` }}>
      <th style={{ padding: '2mm 3mm', textAlign: 'left', fontSize: '7pt', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, width: '11%' }}>Slot</th>
      <th style={{ padding: '2mm 3mm', textAlign: 'left', fontSize: '7pt', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, width: '14%' }}>Pillar</th>
      <th style={{ padding: '2mm 3mm', textAlign: 'left', fontSize: '7pt', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted }}>Hook + body angle</th>
      <th style={{ padding: '2mm 3mm', textAlign: 'left', fontSize: '7pt', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, width: '18%' }}>Format + CTA</th>
    </tr>
  </thead>
);

const PILLAR_COLORS = {
  P1: COLORS.magenta,
  P2: COLORS.emerald,
  P3: COLORS.navy,
  P4: COLORS.gold,
  P5: COLORS.magentaDeep,
  P6: '#7B5CFF',
};

// ─── Page 2, LinkedIn Month 1 (Weeks 1-4) ───────────────────────────
const PageLinkedInM1 = () => {
  const posts = [
    { w: 1, d: 'MON', p: 'P1 · EXCEL', h: 'Spreadsheets are not traceability.', b: 'They\'re a record. Traceability is a chain. The day FSAI knocks, you find out which one you had.', f: 'Text · 4 lines', c: 'Book a 20-min call →' },
    { w: 1, d: 'WED', p: 'P2 · FLOOR', h: 'How Cashel Blue replaced 12 months of Excel in 30 days.', b: '5-slide carousel: the workbook · the pain point · the migration plan · the go-live week · what changed.', f: 'Carousel · 5 slides', c: 'Read the case study →' },
    { w: 1, d: 'FRI', p: 'P4 · RESOURCE', h: 'FSAI audit prep checklist, free 4-page PDF.', b: '27 questions an Irish food inspector will actually ask, drawn from real FSAI audit reports 2024-25.', f: 'PDF lead magnet', c: 'Download (no signup) →' },
    { w: 2, d: 'MON', p: 'P3 · COMPLIANCE', h: 'BRCGS traceability standards: what changed and what nobody is telling you.', b: 'Plain-English breakdown. Three operational implications. One thing that quietly disappeared.', f: 'Text · long-form', c: 'Save for audit prep →' },
    { w: 2, d: 'WED', p: 'P1 · EXCEL', h: 'Saturday morning recall. Tuesday morning explanation. Choose one.', b: 'Punchy image post: stopwatch graphic. "Excel users: avg 4-6 hours to compile recall pack. Keystone users: 4 minutes."', f: 'Image · 1 frame', c: 'See the demo →' },
    { w: 2, d: 'FRI', p: 'P6 · DEMO', h: '60-second mock recall: Excel vs Keystone, same scenario.', b: 'Screen recording. Identical inputs. One side: 11 tabs of frantic copy-paste. Other side: 4 clicks.', f: 'Video · 60 sec', c: 'Watch (no sound needed) →' },
    { w: 3, d: 'MON', p: 'P3 · COMPLIANCE', h: '5 questions every food auditor asks. 4 of them assume you have software.', b: 'Carousel, each slide one question, one answer for spreadsheet shops, one answer for software shops.', f: 'Carousel · 6 slides', c: 'DM "AUDIT" for full PDF →' },
    { w: 3, d: 'WED', p: 'P2 · FLOOR', h: 'What 40+ years of farmhouse production taught us about software.', b: 'Founder-narrated story about the Fethard floor walk that became Keystone\'s product spec.', f: 'Text + image', c: 'Read the long version →' },
    { w: 3, d: 'FRI', p: 'P4 · RESOURCE', h: 'The cost of one missed batch, calculator inside.', b: 'Link to /vs-spreadsheets ROI calculator. Show the maths: batch value × 3% error rate × 12 months.', f: 'Link post + image', c: 'Run your numbers →' },
    { w: 4, d: 'MON', p: 'P1 · EXCEL', h: 'Why per-user pricing kills small dairies.', b: 'Opinion post. If your software charges €30/user/month, you\'re punishing the manufacturer for hiring a second QA person.', f: 'Text · opinion', c: 'See our pricing →' },
    { w: 4, d: 'WED', p: 'P2 · FLOOR', h: 'Three Irish producers. Three different Excel disasters. Same week.', b: 'Anonymised carousel. Names removed, regions kept. Tesco PO numbers, Lidl barcode formats, BRCGS auditors, composite.', f: 'Carousel · 4 slides', c: 'Tell us your worst →' },
    { w: 4, d: 'FRI', p: 'P5 · FOUNDER', h: 'Asif\'s 18 months on the Cashel Blue floor, 7 things he didn\'t expect.', b: 'Long-form first-person. Make-day temperature logs. The phantom 12kg wheel. The Tuesday morning ritual.', f: 'Text · long-form', c: 'Walk a floor with us →' },
  ];
  return (
    <Page bg="#fff">
      <PageHeader pageNum="02" />
      <div style={{ marginTop: '12mm' }}>
        <SectionLabel>LinkedIn · Month 1, Weeks 1-4</SectionLabel>
        <h2 style={{ fontSize: '20pt', fontWeight: 800, color: COLORS.navy, margin: '0 0 4mm 0', letterSpacing: '-0.02em', lineHeight: 1.05 }}>
          Establish the voice. <span style={{ color: COLORS.magenta }}>Earn the first 50 followers.</span>
        </h2>
        <p style={{ fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.5, maxWidth: '180mm', marginBottom: '4mm' }}>
          Month 1 mixes pain (P1) and proof (P2) heavily, establishes "this person actually knows food manufacturing" before any product pitch. Friday posts are always founder voice or visual demos, they\'re the highest-engagement slots, save them for the show-not-tell content.
        </p>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '3mm' }}>
        <LiTableHead />
        <tbody>
          {posts.map((p, i) => (
            <LiPostRow
              key={i}
              week={p.w}
              day={p.d}
              pillar={p.p}
              hook={p.h}
              body={p.b}
              cta={p.c}
              format={p.f}
              pillarColor={PILLAR_COLORS[p.p.split(' ')[0]]}
            />
          ))}
        </tbody>
      </table>
      <PageFooter />
    </Page>
  );
};

// ─── Page 3, LinkedIn Month 2 (Weeks 5-8) ───────────────────────────
const PageLinkedInM2 = () => {
  const posts = [
    { w: 5, d: 'MON', p: 'P1 · EXCEL', h: "If you can't trace it back to the milk intake, you can't prove anything.", b: 'The traceability chain is only as strong as its weakest link, and the weakest link is always the silo→milk-intake jump.', f: 'Text · 3 lines', c: 'See the silo→batch chain →' },
    { w: 5, d: 'WED', p: 'P3 · COMPLIANCE', h: 'EU food safety regulations in plain English.', b: 'Article 18 paragraph by paragraph. What "one step forward, one step back" actually means for an Irish cheesemaker.', f: 'Carousel · 7 slides', c: 'Save this →' },
    { w: 5, d: 'FRI', p: 'P2 · FLOOR', h: 'How a Tipperary creamery cut dispatch errors to zero in 90 days.', b: 'Numbers-driven story. Before: ~3 dispatch corrections/month. After: 0 for 90 straight days. Why? Customer-mapped SKUs.', f: 'Text + chart', c: 'Read the case study →' },
    { w: 6, d: 'MON', p: 'P1 · EXCEL', h: 'Your QA manager is a single point of failure. Software is the redundancy.', b: 'When she goes on holiday, or maternity, what happens to your audit-readiness? Three options. Two of them are illusions.', f: 'Text · opinion', c: 'Book a 20-min call →' },
    { w: 6, d: 'WED', p: 'P3 · COMPLIANCE', h: 'The 2 spreadsheets every dairy has, and why both will fail.', b: 'The Master Batch Sheet and The Dispatch Workbook. Each has a specific failure mode. Each is fixable.', f: 'Image post · diagram', c: 'See the audit-ready version →' },
    { w: 6, d: 'FRI', p: 'P6 · DEMO', h: '60-sec demo: Tesco dispatch note printed in 4 seconds.', b: 'Screen recording. Live data. Tesco-spec template. Pallet label printed in the same click.', f: 'Video · 60 sec', c: 'Watch the workflow →' },
    { w: 7, d: 'MON', p: 'P3 · COMPLIANCE', h: 'What "audit-ready" actually means in 2026.', b: 'Five concrete tests. If your operation passes 4/5 you\'re ready. If you pass 3/5 you\'re hoping. If you pass 2/5 you\'re going to fail.', f: 'Text · long-form', c: 'Self-assess →' },
    { w: 7, d: 'WED', p: 'P4 · RESOURCE', h: 'Free template: BRCGS §3.9 mock recall test record.', b: 'Auditor-accepted format. Pre-filled with example data. PDF + editable Word version.', f: 'PDF + DOCX', c: 'Download (no signup) →' },
    { w: 7, d: 'FRI', p: 'P5 · FOUNDER', h: 'I walked the floor in Nov 2024. Built Keystone in Mar 2025. Here\'s what I changed in between.', b: 'Long-form. Specific design decisions that came from specific moments on the floor. Variable-weight wheels. Salt lot 2200.', f: 'Text · long-form', c: 'Walk a floor with us →' },
    { w: 8, d: 'MON', p: 'P1 · EXCEL', h: 'Excel users vs Keystone users, recall drill time, head to head.', b: 'Visual comparison graphic. Same starting point (one salt lot recall). Excel: 4h 38m. Keystone: 4m 12s.', f: 'Image · 1 frame', c: 'Run your own mock →' },
    { w: 8, d: 'WED', p: 'P2 · FLOOR', h: 'Every feature in Keystone came from a real problem on a real Irish dairy floor.', b: 'Long-form. Three concrete examples of features shipped in direct response to Cashel Blue floor feedback. Reference-call CTA in place of a fabricated pull-quote.', f: 'Text · long-form', c: 'Read the case study →' },
    { w: 8, d: 'FRI', p: 'P6 · DEMO', h: 'Pricing teardown: MRPeasy · Wherefour · SoftTrace · Keystone.', b: '4-slide carousel. Honest comparison. Where each competitor wins. Where Keystone wins. No mud-slinging.', f: 'Carousel · 4 slides', c: 'See the full comparison →' },
  ];
  return (
    <Page bg="#fff">
      <PageHeader pageNum="03" />
      <div style={{ marginTop: '12mm' }}>
        <SectionLabel>LinkedIn · Month 2, Weeks 5-8</SectionLabel>
        <h2 style={{ fontSize: '20pt', fontWeight: 800, color: COLORS.navy, margin: '0 0 4mm 0', letterSpacing: '-0.02em', lineHeight: 1.05 }}>
          Convert curious to interested. <span style={{ color: COLORS.magenta }}>First 5 demos booked.</span>
        </h2>
        <p style={{ fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.5, maxWidth: '180mm', marginBottom: '4mm' }}>
          Month 2 raises the conversion weight. Increase P3 (compliance) and P6 (demo) frequency. The audience already knows you understand them, now show them concretely what they\'re missing. Pricing teardown in W8 is deliberately late, it lands harder once they\'ve seen the value.
        </p>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '3mm' }}>
        <LiTableHead />
        <tbody>
          {posts.map((p, i) => (
            <LiPostRow
              key={i}
              week={p.w}
              day={p.d}
              pillar={p.p}
              hook={p.h}
              body={p.b}
              cta={p.c}
              format={p.f}
              pillarColor={PILLAR_COLORS[p.p.split(' ')[0]]}
            />
          ))}
        </tbody>
      </table>
      <PageFooter />
    </Page>
  );
};

// ─── Page 4, LinkedIn Month 3 (Weeks 9-12) ──────────────────────────
const PageLinkedInM3 = () => {
  const posts = [
    { w: 9, d: 'MON', p: 'P1 · EXCEL', h: 'If your software can\'t print a Tesco-spec pallet label, it isn\'t traceability.', b: 'It\'s a database with extra steps. Three things real Irish-retailer traceability software must do at the dispatch bay.', f: 'Text + image', c: 'See the pallet label demo →' },
    { w: 9, d: 'WED', p: 'P3 · COMPLIANCE', h: 'FSAI mock recall, what they actually test, slide by slide.', b: 'Carousel based on 14 anonymised FSAI mock recall reports. The questions they ask. The evidence they accept.', f: 'Carousel · 8 slides', c: 'DM "MOCK" for PDF →' },
    { w: 9, d: 'FRI', p: 'P6 · DEMO', h: 'Live tour: dispatch + traceability + COA in one screen.', b: 'Founder-narrated. Real customer data (anonymised). The "single screen" promise made concrete.', f: 'Video · 90 sec', c: 'Book a tailored walkthrough →' },
    { w: 10, d: 'MON', p: 'P5 · FOUNDER', h: 'Why "designed-to-support" is the only honest compliance claim software can make.', b: 'Opinion piece. Software can\'t be FSAI-certified, your operation can. Anyone who says otherwise is selling theatre.', f: 'Text · long-form', c: 'Read the full take →' },
    { w: 10, d: 'WED', p: 'P2 · FLOOR', h: 'Tipperary, Wicklow, West Cork, three Irish brands, one platform.', b: 'Light-touch logo strip. Permission-granted only. Operational similarities that surprised us; differences that drove product decisions.', f: 'Image · logo strip', c: 'Become the next one →' },
    { w: 10, d: 'FRI', p: 'P4 · RESOURCE', h: 'Free download: 12-month dairy production planning template.', b: 'A4 wall-planner format. Seasonality, BRCGS audit slots, FSAI inspection window, holiday cover.', f: 'PDF · A4 wall format', c: 'Download (no signup) →' },
    { w: 11, d: 'MON', p: 'P1 · EXCEL', h: 'What happens to your dairy when your QA manager goes on maternity leave?', b: 'Long-form. Real Irish scenario. Three operational pillars she holds up that no spreadsheet documents. Solution path.', f: 'Text · long-form', c: 'Book a 20-min call →' },
    { w: 11, d: 'WED', p: 'P3 · COMPLIANCE', h: 'BRCGS vs Issue 10, what is already in the consultation draft.', b: 'Early-warning carousel. Three changes Irish dairy producers should start preparing for now, not in 2027.', f: 'Carousel · 5 slides', c: 'Save for compliance prep →' },
    { w: 11, d: 'FRI', p: 'P6 · DEMO', h: '60-sec demo: customer-mapped SKUs in action.', b: 'Screen recording. One internal cheese SKU. Five retailer-specific dispatch presentations. One click each.', f: 'Video · 60 sec', c: 'See the SKU mapping flow →' },
    { w: 12, d: 'MON', p: 'P1 · EXCEL', h: '5 Irish dairies that will NOT be Excel-based by Christmas.', b: 'Light teasing. Don\'t name names. Frame as "the boats are moving", social proof + scarcity.', f: 'Text · 3 lines', c: 'Be the 6th →' },
    { w: 12, d: 'WED', p: 'P5 · FOUNDER', h: 'How we wrote the privacy policy without a DPO.', b: 'Long-form. Plain-language privacy posture, why CloudVoro doesn\'t have a formal DPO, and why that\'s actually GDPR-honest.', f: 'Text · long-form', c: 'Read the policy →' },
    { w: 12, d: 'FRI', p: 'P2 · FLOOR', h: 'Q1 wrap, every post, every learning, every customer signed.', b: '12-slide retrospective carousel. Numbers (engagement, demos, signups). Top 3 posts. Top 3 lessons. What changes in Q2.', f: 'Carousel · 12 slides', c: 'See the Q1 numbers →' },
  ];
  return (
    <Page bg="#fff">
      <PageHeader pageNum="04" />
      <div style={{ marginTop: '12mm' }}>
        <SectionLabel>LinkedIn · Month 3, Weeks 9-12</SectionLabel>
        <h2 style={{ fontSize: '20pt', fontWeight: 800, color: COLORS.navy, margin: '0 0 4mm 0', letterSpacing: '-0.02em', lineHeight: 1.05 }}>
          Land the first 2-3 paying customers. <span style={{ color: COLORS.magenta }}>Set up Q2.</span>
        </h2>
        <p style={{ fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.5, maxWidth: '180mm', marginBottom: '4mm' }}>
          Month 3 leans into social proof (P2) and founder authority (P5). W11 is "scenario week", pick three real operational scenarios Irish dairies will face this year and show solutions. W12 is reflection, a public Q1 retrospective signals momentum and creates anchor content to point all of Q2 back to.
        </p>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '3mm' }}>
        <LiTableHead />
        <tbody>
          {posts.map((p, i) => (
            <LiPostRow
              key={i}
              week={p.w}
              day={p.d}
              pillar={p.p}
              hook={p.h}
              body={p.b}
              cta={p.c}
              format={p.f}
              pillarColor={PILLAR_COLORS[p.p.split(' ')[0]]}
            />
          ))}
        </tbody>
      </table>
      <PageFooter />
    </Page>
  );
};

// ─── Page 5, Instagram + execution guide ────────────────────────────
const PageInstagram = () => {
  const pillars = [
    {
      n: '01',
      name: 'Floor First',
      colour: COLORS.magenta,
      cadence: 'Tue · 1-2/wk',
      summary: 'Asif on production floors, shadowing QA, mid-conversation BTS, walking the line. Always real. Never staged.',
      examples: [
        'Asif at the Cashel Blue make-day, 6:30am light, navy quarter-zip, cheese cloth in foreground',
        'Hand on a stainless-steel silo control panel, focus on the temperature gauge, blurred Tipperary morning behind',
        'QA notebook close-up, Marzena\'s handwriting, batch numbers visible, coffee ring on corner',
        'Asif laughing with Louis at dispatch, caught moment, no eye-contact with camera',
      ],
    },
    {
      n: '02',
      name: 'Tools, Not Theatre',
      colour: '#7B5CFF',
      cadence: 'Thu · 1/wk',
      summary: 'Close-ups of Keystone screens running on real factory hardware. Macbook on a stainless prep table. iPad in a wet-area sleeve. Never the marketing-render version.',
      examples: [
        'Backward trace screen mid-resolution, finger pointing at one ingredient lot row',
        'iPad locked to the silo-tracking app, gloved hand reaching for it from a wash-down hose',
        'Dispatch note rolling off the warehouse Zebra printer, Tesco PO visible',
        'Two screens side by side, old Excel workbook vs Keystone, identical batch data',
      ],
    },
    {
      n: '03',
      name: 'Cheese is Craft',
      colour: COLORS.emerald,
      cadence: 'Sat · 1/wk',
      summary: 'Beauty shots of finished product, Cashel Blue®, Crozier Blue® (with written permission). The aesthetic Instagram-y content that builds brand affinity without selling software.',
      examples: [
        'Wheel cross-section on a wooden board, slate-grey background, flash of green vein',
        'Crozier Blue® wedge on a copper tray, natural window light, soft shadow',
        'Pallet of finished wheels in the cold room, mood lighting, condensation on a hand-lettered crate',
        'Sarah and Sergio at the cellar door (with their explicit OK on the specific image)',
      ],
    },
    {
      n: '04',
      name: 'Excel vs Reality',
      colour: COLORS.gold,
      cadence: 'Tue · 1/wk',
      summary: 'Side-by-side photos: spreadsheet chaos on one side, Keystone\'s clarity on the other. Educational. Sharable. Performs well in DMs.',
      examples: [
        'Print-out of a 28-tab Excel workbook taped to a wall vs Keystone dashboard on screen',
        'QA office desk: paper batch sheets and pink sticky notes vs single iPad propped on the same desk',
        'Whiteboard with handwritten recall checklist vs the auto-generated PDF response pack',
        'Handwritten dispatch run-sheet (Sharpie-on-paper) vs Keystone dispatch screen with same data',
      ],
    },
    {
      n: '05',
      name: 'The Team',
      colour: COLORS.navyDeep,
      cadence: 'Quarterly · 4/quarter',
      summary: 'Humans behind the product and the customers. Always with explicit per-image permission from the subject. Caption format: name, role, why they matter.',
      examples: [
        'Asif portrait at his Limerick desk, unposed, mid-thought',
        'Sarah Furno at the Cashel Blue counter (permissioned image only)',
        'Louis on the dispatch dock, clipboard in hand, smiling',
        'The CloudVoro slack room at 11pm during a go-live, laptops, kanban board, half-eaten pizza',
      ],
    },
  ];
  return (
    <Page bg="#fff" last>
      <PageHeader pageNum="05" />
      <div style={{ marginTop: '12mm' }}>
        <SectionLabel>Instagram · Visual pillars + execution</SectionLabel>
        <h2 style={{ fontSize: '20pt', fontWeight: 800, color: COLORS.navy, margin: '0 0 3mm 0', letterSpacing: '-0.02em', lineHeight: 1.05 }}>
          Build the visual brand. <span style={{ color: COLORS.magenta }}>5 pillars. Photo-first.</span>
        </h2>
        <p style={{ fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.5, maxWidth: '180mm', marginBottom: '4mm' }}>
          Instagram is not for selling software, it\'s for building brand gravity around a quietly confident, floor-first identity. Cadence: 2-3 posts/week, organised by pillar not date. Never use stock photos. Always permissioned for any identifiable person or branded product.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '3mm', marginTop: '2mm' }}>
        {pillars.map((p) => (
          <div
            key={p.n}
            style={{
              display: 'grid',
              gridTemplateColumns: '15mm 1fr 1.4fr',
              gap: '4mm',
              padding: '3mm 4mm',
              background: COLORS.bgSoft,
              border: `1px solid ${COLORS.border}`,
              borderLeft: `3px solid ${p.colour}`,
              borderRadius: 4,
            }}
          >
            <div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, color: p.colour, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {p.n}
              </div>
              <div style={{ fontSize: '6.5pt', fontWeight: 700, color: COLORS.muted, marginTop: '1mm', letterSpacing: '0.06em' }}>
                {p.cadence}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10pt', fontWeight: 800, color: COLORS.navy, marginBottom: '1.5mm', letterSpacing: '-0.005em' }}>
                {p.name}
              </div>
              <div style={{ fontSize: '7.5pt', color: COLORS.slate, lineHeight: 1.45 }}>
                {p.summary}
              </div>
            </div>
            <ul style={{ fontSize: '7pt', color: COLORS.slate, lineHeight: 1.5, paddingLeft: 12, margin: 0 }}>
              {p.examples.map((e, i) => (
                <li key={i} style={{ marginBottom: '0.6mm' }}>{e}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Hashtag bank + execution rules */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '5mm', marginTop: '4mm' }}>
        <div style={{ padding: '3mm 4mm', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 4 }}>
          <div style={{ fontSize: '7.5pt', fontWeight: 800, color: COLORS.navy, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2mm' }}>
            Hashtag bank
          </div>
          <div style={{ fontSize: '7pt', color: COLORS.slate, lineHeight: 1.55 }}>
            <strong style={{ color: COLORS.navy }}>Always:</strong> #IrishFood · #FoodManufacturing · #Traceability · #BatchTraceability · #FoodSafety<br />
            <strong style={{ color: COLORS.navy }}>Sector-specific:</strong> #IrishDairy · #FarmhouseCheese · #ArtisanCheese · #DairyProcessing · #IrishCheese<br />
            <strong style={{ color: COLORS.navy }}>Compliance:</strong> #BRCGS · #FSAI · #HACCP · #FoodCompliance · #AuditReady<br />
            <strong style={{ color: COLORS.navy }}>Regional:</strong> #Tipperary · #IrishBusiness · #BordBia · #MadeInIreland<br />
            <strong style={{ color: COLORS.navy }}>SaaS / B2B (LinkedIn cross-post):</strong> #SaaS · #B2BSoftware · #FoodTech · #ManufacturingSoftware
          </div>
        </div>
        <div style={{ padding: '3mm 4mm', background: COLORS.cream, border: `1px solid ${COLORS.gold}`, borderRadius: 4 }}>
          <div style={{ fontSize: '7.5pt', fontWeight: 800, color: COLORS.navy, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2mm' }}>
            Execution rules
          </div>
          <ul style={{ fontSize: '7pt', color: COLORS.slate, lineHeight: 1.55, paddingLeft: 12, margin: 0 }}>
            <li>Never use stock photos, ever.</li>
            <li>Always get explicit, image-specific permission for identifiable people or branded products.</li>
            <li>One platform-native asset per post, no cross-posted carousels.</li>
            <li>Schedule LinkedIn for Mon/Wed/Fri 08:15 Dublin time.</li>
            <li>Schedule Instagram for Tue/Thu/Sat 18:00 Dublin time.</li>
            <li>Reply to every comment within 4 working hours for the first 8 weeks.</li>
            <li>Track DMs as the real conversion metric, not likes.</li>
          </ul>
        </div>
      </div>

      <PageFooter />
    </Page>
  );
};

// ─── Toolbar (links back to brochure + site) ─────────────────────────
const Toolbar = () => (
  <div
    className="playbook-toolbar no-print"
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: '#fff',
      borderBottom: `1px solid ${COLORS.border}`,
      padding: '12px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 8px rgba(10, 37, 64, 0.06)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <a
        href="/brochure/sales"
        data-testid="playbook-back-to-brochure"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          background: COLORS.bgSoft,
          color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          fontSize: 12.5,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        ← Back to brochure
      </a>
      <div
        style={{
          borderLeft: `1px solid ${COLORS.border}`,
          paddingLeft: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <KeystoneLogo height={36} variant="light" showTagline={false} />
        <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Marketing Playbook</div>
          <div style={{ fontSize: 11, color: COLORS.muted }}>5 pages · A4 · 12-week rolling · Internal</div>
        </div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <a
        href="/brochure/sales-primer"
        data-testid="playbook-sales-primer-link"
        title="Internal · Multi-lingual sales primer (EN · AR · UK · RU)"
        style={{
          padding: '9px 14px',
          background: '#fff',
          color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        🌐  Sales Primer
      </a>
      <button
        onClick={() => {
          const el = document.querySelector('[data-testid="linkedin-copy-library"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        data-testid="playbook-jump-to-copy"
        style={{
          background: COLORS.navy,
          color: '#fff',
          border: 'none',
          padding: '10px 18px',
          borderRadius: 6,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        ↓  Copy library
      </button>
      <button
        onClick={() => window.print()}
        data-testid="playbook-print-btn"
        style={{
          background: COLORS.magenta,
          color: '#fff',
          border: 'none',
          padding: '10px 22px',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          letterSpacing: '0.02em',
          boxShadow: '0 4px 12px rgba(214, 51, 108, 0.3)',
        }}
      >
        🖨  Print / Save PDF
      </button>
    </div>
  </div>
);

function PlaybookContent() {
  return (
    <div style={{ background: '#E5E9F0', minHeight: '100vh' }}>
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .playbook-page {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always;
            break-after: page;
          }
          .playbook-page:last-of-type { page-break-after: auto; break-after: auto; }
          .playbook-stack { padding: 0 !important; }
        }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      `}</style>

      <Toolbar />

      <div className="playbook-stack" style={{ padding: '32px 16px 64px' }}>
        <PageCover />
        <PageLinkedInM1 />
        <PageLinkedInM2 />
        <PageLinkedInM3 />
        <PageInstagram />
      </div>

      {/* Ready-to-publish post copy library, scroll-only, hidden from print */}
      <LinkedInCopyLibrary />
      <InstagramCaptionLibrary />
    </div>
  );
}

export default function MarketingPlaybook() {
  return (
    <BrochureGate
      title="Marketing playbook · internal only."
      subtitle="This is the Keystone 12-week LinkedIn + Instagram editorial plan. Internal use only, not for prospects. Enter the access password to continue."
      buttonLabel="Unlock playbook"
      eyebrow="Keystone by CloudVoro · Internal"
      tokenKey="kx.pb.tk"
    >
      <PlaybookContent />
    </BrochureGate>
  );
}
